import { RootStoreSession } from "../root-store";
import { computed, makeObservable, observable } from "mobx";
import { CreationProject, UsageProject } from "../project/project";
import { CreationAreaDto, UsageAreaDto } from "./area-dto";
import { Nullable, Optional } from "../../globals/types";
import { DataNotLoaded } from "../../globals/errors";
import moment from "moment";
import { Constants } from "../../globals/constants";
import { ActivityLogEntry } from "../activity-log-entry/activity-log-entry-store";
import _ from "lodash";
import { desc, label } from "../../reflection/reflection";

export abstract class Area {
    rootStore: RootStoreSession;

    protected constructor(rootStore: RootStoreSession) {
        this.rootStore = rootStore;
        makeObservable(this, {
            id: computed,
            projectId: computed,
        })
    }

    abstract get id(): number;

    abstract get projectId(): number;
}

export class CreationArea extends Area {
    private updatedAtTimestamp_?: number;
    areaDto: CreationAreaDto;

    constructor(areaDto: CreationAreaDto, rootStore: RootStoreSession) {
        super(rootStore);
        this.areaDto = areaDto;
        makeObservable(this, {
            areaDto: observable,
            extId: computed,
            project: computed,
            areaExact: computed,
            areaRounded: computed,
            effectiveAreaRounded: computed,
            nek: computed,
            stage: computed,
            png: computed,
            pngDisplayText: computed,
            eligibility: computed,
            eligibilityDisplayText: computed,
            soilWorkStartDate: computed,
            soilWorkStartDateDisplayText: computed,
            soilWorkEndDate: computed,
            soilWorkEndDateDisplayText: computed,
            afterSoilApplicationAcceptanceDate: computed,
            afterSoilApplicationAcceptanceDateDisplayText: computed,
            cultivationEndDate: computed,
            cultivationEndDateDisplayText: computed,
            afterCultivationAcceptanceDate: computed,
            afterCultivationAcceptanceDateDisplayText: computed,
            afterSoilApplicationDocStatus: computed,
            afterSoilApplicationDocSource: computed,
            afterCultivationDocStatus: computed,
            afterCultivationDocSource: computed,
            creationStatus: computed,
            remarksProjectStatus: computed,
            remarksDocumentation: computed,
            updatedAtTimestamp: computed,
            updatedAt: computed
        })
    }

    @label("ID")
    get id(): number {
        return this.areaDto.id;
    }

    get extId(): Optional<number> {
        return this.areaDto.extId ?? undefined;
    }

    @label("Projekt-ID")
    get projectId(): number {
        return this.areaDto.fffCreationProjectId;
    }

    get project(): CreationProject {
        const project = this.rootStore.projectStore.creationProjects.find(p => p.id === this.projectId);

        if (project !== undefined) {
            return project;
        }

        throw new DataNotLoaded("Creation Project not loaded");
    }

    get areaExact(): number {
        return this.areaDto.areaExact;
    }

    get areaRounded(): number {
        return this.areaDto.areaRounded;
    }

    @label("Fläche [m²]")
    get effectiveAreaRounded(): number {
        return this.areaDto.effectiveAreaRounded;
    }

    @label("Realisierte NEK")
    @desc("Nutzungseignungsklasse (NEK). Es ist immer der aktuelle Zustand einzutragen. In der Projektbearbeitungsphase ist dies beispielsweise der Zielzustand. Nach Folgebewirtschaftung und erfolgter Abnahme durch das uwe der Endzustand.")
    get nek(): number {
        return this.areaDto.nek;
    }

    @label("Etappe")
    @desc("Etappe der Aufwertung / Rekultivierung.")
    get stage(): Optional<number> {
        return this.areaDto.stage ?? undefined;
    }

    get png(): Optional<number> {
        return this.areaDto.png ?? undefined;
    }

    @label("Realisierte PNG [cm]")
    @desc("Pflanzennutzbare Gründigkeit (PNG). Es ist immer der aktuelle Zustand einzutragen. In der Projektbearbeitungsphase ist dies beispielsweise der Zielzustand. Nach Folgebewirtschaftung und erfolgter Abnahme durch das uwe der Endzustand.")
    get pngDisplayText(): string {
        return this.areaDto.png !== null ? this.areaDto.png.toString() : "";
    }

    get eligibility(): number {
        return this.areaDto.eligibility;
    }

    @label("Anrechenbarkeit")
    get eligibilityDisplayText(): string {
        return this.rootStore.keyValueStore.getEligibilityValueDisplayText(this.areaDto.eligibility);
    }

    get soilWorkStartDate(): Nullable<Date> {
        return this.areaDto.soilWorkStartDate !== null
            ? moment(this.areaDto.soilWorkStartDate).toDate()
            : null;
    }

    @label("Beginn Bodenarbeiten")
    get soilWorkStartDateDisplayText(): string {
        return this.areaDto.soilWorkStartDate !== null
            ? moment(this.areaDto.soilWorkStartDate).format(Constants.dateFormat)
            : "";
    }

    get soilWorkEndDate(): Nullable<Date> {
        return this.areaDto.soilWorkEndDate !== null
            ? moment(this.areaDto.soilWorkEndDate).toDate()
            : null;
    }

    @label("Ende Bodenarbeiten")
    get soilWorkEndDateDisplayText(): string {
        return this.areaDto.soilWorkEndDate !== null
            ? moment(this.areaDto.soilWorkEndDate).format(Constants.dateFormat)
            : "";
    }

    get afterSoilApplicationAcceptanceDate(): Nullable<Date> {
        return this.areaDto.afterSoilApplicationAcceptanceDate !== null
            ? moment(this.areaDto.afterSoilApplicationAcceptanceDate).toDate()
            : null;
    }

    @label("Abnahme nach Bodenauftrag")
    get afterSoilApplicationAcceptanceDateDisplayText(): string {
        return this.areaDto.afterSoilApplicationAcceptanceDate !== null
            ? moment(this.areaDto.afterSoilApplicationAcceptanceDate).format(Constants.dateFormat)
            : "";
    }

    get cultivationEndDate(): Nullable<Date> {
        return this.areaDto.cultivationEndDate !== null
            ? moment(this.areaDto.cultivationEndDate).toDate()
            : null;
    }

    @label("Ende Folgebewirtschaftung")
    get cultivationEndDateDisplayText(): string {
        return this.areaDto.cultivationEndDate !== null
            ? moment(this.areaDto.cultivationEndDate).format(Constants.dateFormat)
            : "";
    }

    get afterCultivationAcceptanceDate(): Nullable<Date> {
        return this.areaDto.afterCultivationAcceptanceDate !== null
            ? moment(this.areaDto.afterCultivationAcceptanceDate).toDate()
            : null;
    }

    @label("Abnahme nach Folgebewirtschaftung")
    get afterCultivationAcceptanceDateDisplayText(): string {
        return this.areaDto.afterCultivationAcceptanceDate !== null
            ? moment(this.areaDto.afterCultivationAcceptanceDate).format(Constants.dateFormat)
            : "";
    }

    @label("Stand Dokumentation nach Bodenauftrag")
    @desc("Auflagenkontrolle: Wurde der Bodenauftrag dokumentiert?")
    get afterSoilApplicationDocStatus(): string {
        return this.rootStore.keyValueStore.getConditionControlStatusDisplayText(this.areaDto.afterSoilApplicationDocStatus ?? undefined);
    }

    @label("Quelle Dokumentation nach Bodenauftrag")
    @desc("Auflagenkontrolle: In welchem Dokument wurde eine Dokumentation nach Bodenauftrag gefordert?")
    get afterSoilApplicationDocSource(): string {
        return this.rootStore.keyValueStore.getConditionControlSourceDisplayText(this.areaDto.afterSoilApplicationDocSource ?? undefined);
    }

    @label("Stand Dokumentation nach Folgebewirtschaftung")
    @desc("Auflagenkontrolle: Wurde ein Protokoll oder eine Dokumentation nach der Folgebewirtschaftung erstellt?")
    get afterCultivationDocStatus(): string {
        return this.rootStore.keyValueStore.getConditionControlStatusDisplayText(this.areaDto.afterCultivationDocStatus ?? undefined);
    }

    @label("Quelle Dokumentation nach Folgebewirtschaftung")
    @desc("Auflagenkontrolle: In welchem Dokument wurde eine Dokumentation nach der Folgebewirtschaftung gefordert?")
    get afterCultivationDocSource(): string {
        return this.rootStore.keyValueStore.getConditionControlSourceDisplayText(this.areaDto.afterCultivationDocSource ?? undefined);
    }

    @label("Status Schaffung")
    get creationStatus(): string {
        return this.rootStore.keyValueStore.getCompensationStatusDisplayText(this.areaDto.creationStatus ?? undefined);
    }

    @label("Bemerkungen Projektstand")
    get remarksProjectStatus(): Optional<string> {
        return this.areaDto.remarksProjectStatus ?? undefined;
    }

    @label("Bemerkungen Dokumentation")
    get remarksDocumentation(): Optional<string> {
        return this.areaDto.remarksDocumentation ?? undefined;
    }

    get updatedAtTimestamp(): number {
        if (this.updatedAtTimestamp_ === undefined) {
            const updateEntries = this.rootStore.activityLogEntryStore.activityLogEntries[Constants.creationAreaExtTableName]
                .filter(e => e.rowId === this.extId && e.action === "update");
            const sortedUpdateEntries: ActivityLogEntry[] = _.sortBy(updateEntries, ["timestamp"]);
            const timestamp = _.last<ActivityLogEntry>(sortedUpdateEntries)?.timestamp;

            if (timestamp !== undefined) {
                this.updatedAtTimestamp_ = timestamp;
            } else {
                this.updatedAtTimestamp_ = -1;
            }
        }

        return this.updatedAtTimestamp_!;
    }

    get updatedAt(): string {
        if (this.updatedAtTimestamp_ !== -1) {
            return moment.unix(this.updatedAtTimestamp).format(Constants.timestampFormat);
        }

        return "-";
    }
}

export class UsageArea extends Area {
    areaDto: UsageAreaDto;

    constructor(areaDto: UsageAreaDto, rootStore: RootStoreSession) {
        super(rootStore);
        this.areaDto = areaDto;
        makeObservable(this, {
            areaDto: observable,
            project: computed,
            areaExact: computed,
            areaRounded: computed,
            effectiveAreaRounded: computed,
            areaNEK2: computed,
            areaNEK3: computed,
            areaNEK4: computed,
            areaNEK5: computed,
            areaNEK6: computed,
            areaNEKUnknown: computed,
            effectiveAreaNEK2Rounded: computed,
            effectiveAreaNEK3Rounded: computed,
            effectiveAreaNEK4Rounded: computed,
            effectiveAreaNEK5Rounded: computed,
            effectiveAreaNEK6Rounded: computed,
            effectiveAreaNEKUnknownRounded: computed,
            eligibility: computed,
        })
    }

    @label("ID")
    get id(): number {
        return this.areaDto.id;
    }

    @label("Projekt-ID")
    get projectId(): number {
        return this.areaDto.fffUsageProjectId;
    }

    get project(): UsageProject {
        const project = this.rootStore.projectStore.usageProjects.find(p => p.id === this.projectId);

        if (project !== undefined) {
            return project;
        }

        throw new DataNotLoaded("Usage Project not loaded");
    }

    get areaExact(): number {
        return this.areaDto.areaExact;
    }

    get areaRounded(): number {
        return this.areaDto.areaRounded;
    }

    @label("Fläche total [m²]")
    get effectiveAreaRounded(): number {
        return this.areaDto.effectiveAreaRounded;
    }

    @label("NEK 2 [m²]")
    get areaNEK2(): Optional<number> {
        return this.areaDto.areaNEK2 ?? undefined;
    }

    @label("NEK 3 [m²]")
    get areaNEK3(): Optional<number> {
        return this.areaDto.areaNEK3 ?? undefined;
    }

    @label("NEK 4 [m²]")
    get areaNEK4(): Optional<number> {
        return this.areaDto.areaNEK4 ?? undefined;
    }

    @label("NEK 5 [m²]")
    get areaNEK5(): Optional<number> {
        return this.areaDto.areaNEK5 ?? undefined;
    }

    @label("NEK 6 [m²]")
    get areaNEK6(): Optional<number> {
        return this.areaDto.areaNEK6 ?? undefined;
    }

    @label("NEK unbekannt [m²]")
    get areaNEKUnknown(): Optional<number> {
        return this.areaDto.areaNEKUnknown ?? undefined;
    }

    get effectiveAreaNEK2Rounded(): Optional<number> {
        return this.areaDto.effectiveAreaNEK2Rounded ?? undefined;
    }

    get effectiveAreaNEK3Rounded(): Optional<number> {
        return this.areaDto.effectiveAreaNEK3Rounded ?? undefined;
    }

    get effectiveAreaNEK4Rounded(): Optional<number> {
        return this.areaDto.effectiveAreaNEK4Rounded ?? undefined;
    }

    get effectiveAreaNEK5Rounded(): Optional<number> {
        return this.areaDto.effectiveAreaNEK5Rounded ?? undefined;
    }

    get effectiveAreaNEK6Rounded(): Optional<number> {
        return this.areaDto.effectiveAreaNEK6Rounded ?? undefined;
    }

    get effectiveAreaNEKUnknownRounded(): Optional<number> {
        return this.areaDto.effectiveAreaNEKUnknownRounded ?? undefined;
    }

    get eligibility(): number {
        return this.areaDto.eligibility;
    }

    @label("Anrechenbarkeit")
    get eligibilityDisplayText(): string {
        return this.rootStore.keyValueStore.getEligibilityValueDisplayText(this.areaDto.eligibility);
    }
}